/**
 * MatchJourneyCard — every recorded step of one match, oldest first (2026-10-15).
 *
 * Answers "when did each member show interest, decline, or see this match?". Each row
 * is a time, the time since the match was created, and one plain sentence naming who
 * acted. The raw status change (from → to · client · reason) stays under the sentence.
 *
 * Honesty rules:
 *   - A source the API could not read is named ("This journey is incomplete"), so a
 *     partial list is never read as the whole story.
 *   - The footnote names what IS recorded and what is NOT, so a missing step is never
 *     read as "did not happen".
 *   - Every sentence says only what its record proves (2026-10-26, PROD match 96784e0a):
 *       · "made visible" is presented_at, not the status change that created the row
 *         (a summary-gated match stays hidden after it);
 *       · a push is "sent" when it went, not when its card was written (quiet hours
 *         held that one for nine hours), and Apple accepting it is not "displayed";
 *       · an open says whether it came from the push or the in-app list, or that the
 *         app did not record which.
 *
 * Backend contract: GET /v1/admin/matches/:matchId → `journey`
 * (backend/src/services/match-journey.ts). Absent on the compare endpoint. The types
 * below mirror that file; a kind or outcome this card does not know renders a neutral
 * line, never a crash.
 */

export type JourneyActor = 'A' | 'B' | 'system' | 'admin' | 'unknown';
export type JourneyMember = 'A' | 'B';
export type JourneySource = 'status_changes' | 'notifications' | 'push_outbox' | 'app_views' | 'match_facts';
export type NotificationStep = 'sent' | 'received' | 'opened' | 'dismissed' | 'read';
export type OpenedVia = 'push' | 'inbox';

interface PushEventBase {
  at: string;
  kind: 'push';
  actor: JourneyMember;
  notification_id: string;
  notification_type: string;
  /** Absent = not read; null = the member has no time zone on record. */
  time_zone?: string | null;
}

export type JourneyPushEvent = PushEventBase &
  (
    | { outcome: 'held'; reason: string; held_until: string | null; attempts: number; still_held: boolean }
    | { outcome: 'sent'; receipt: string | null; platform: string | null }
    | { outcome: 'cancelled'; outbox_outcome: string | null }
    | { outcome: 'exhausted'; outbox_outcome: string | null; attempts: number }
    | { outcome: 'not_sent'; reason: string | null }
  );

export type JourneyEvent =
  | { at: string; kind: 'created' | 'restarted'; actor: 'system' }
  | {
      at: string;
      kind: 'status';
      actor: JourneyActor;
      from_status: string | null;
      to_status: string;
      triggered_by: string | null;
      client: string | null;
      reason: string | null;
    }
  | {
      at: string;
      kind: 'shown';
      actor: 'system';
      members: ['A', 'B'];
      /** Absent = not read. */
      created_for?: JourneyMember | 'other' | null;
    }
  | { at: string; kind: 'pictures_shared' | 'pictures_viewed'; actor: JourneyMember }
  | {
      at: string;
      kind: 'notification';
      actor: JourneyMember;
      step: NotificationStep;
      notification_type: string;
      push_sent: boolean | null;
      card_suppressed?: boolean;
      via?: OpenedVia | null;
    }
  | JourneyPushEvent
  | { at: string; kind: 'app_view'; actor: JourneyMember; client: string | null };

export interface MatchJourney {
  events: JourneyEvent[];
  unavailable: JourneySource[];
}

interface Names {
  nameA: string;
  nameB: string;
}

interface DescribeContext {
  /** This null → presented transition happened while the match was still hidden. */
  hiddenUntilShown?: boolean;
}

const SOURCE_LABEL: Record<JourneySource, string> = {
  status_changes: 'status changes',
  notifications: 'notifications',
  push_outbox: 'held pushes',
  app_views: 'first app views',
  match_facts: 'match facts (created for, time zones)',
};

/** Under a minute apart is one moment — the same tolerance the backend uses for a restart. */
const SAME_MOMENT_MS = 60_000;

/**
 * Pushes were not stamped on every card before this date: the backend rail stamped
 * push_sent from 2026-08-10 (#2329), the Lambda rail from #2505, which reached main on
 * 2026-08-17. A FALSE push_sent on an older card does not prove no push went.
 */
const PUSH_STAMPED_FROM = '2026-08-18T00:00:00.000Z';

const HOLD_REASON: Record<string, string> = {
  quiet_hours: 'quiet hours',
  noncritical_24h_cap: 'the limit of one non-urgent push per 24 hours',
  cadence_capped: 'the email cadence limit',
  env_skipped: 'the push rail is switched off in this environment',
};

/** core.notification_outbox.outcome of a push that ended without going. */
const OUTBOX_OUTCOME: Record<string, string> = {
  aged_out: 'it expired before it could go',
  attempts: 'it ran out of attempts',
  retry_after_expiry: 'a retry after a send failure would have come after it expired',
  member_gone: 'the member was deleted or suspended, or is a PROD tester',
  stage_resolved: 'it no longer applied (the member had already acted)',
  id_verification_required: 'the member must verify their ID first',
  superseded: 'a newer message replaced it',
  expires_before_wake: 'the hold outlasted the message, so it was never sent',
  no_token: 'no push token',
  opted_out: 'the member turned push notifications off',
  invalid_token: 'the push token is no longer valid',
};

/** data.push_error: why the rail did not send. An unknown code stays raw. */
function pushErrorWords(code: string): string {
  const http = /^http_(\d{3})$/.exec(code);
  if (http) return `the push service answered HTTP ${http[1]}`;
  switch (code) {
    case 'no_token':
      return 'no push token';
    case 'push_opted_out':
    case 'opted_out':
      return 'the member turned push notifications off';
    case 'invalid_token':
      return 'the push token was not valid';
    case 'DeviceNotRegistered':
      return 'the device is no longer registered for pushes';
    case 'expo_error':
      return 'the push service returned an error';
    default:
      return code;
  }
}

function vendorOf(platform: string | null): string {
  if (platform === 'ios') return 'Apple';
  if (platform === 'android') return 'Google';
  return 'Apple or Google';
}

function actorName(actor: unknown, { nameA, nameB }: Names): string | null {
  switch (actor) {
    case 'A':
      return nameA;
    case 'B':
      return nameB;
    case 'admin':
      return 'An admin';
    case 'unknown':
      return 'Unknown actor';
    default:
      return null; // the scheduler / system: the sentence has no subject
  }
}

/** "08:00", or "2026-10-23 10:00" — the wall time of an instant in a zone. Throws on a bad zone. */
function wallTime(instant: string, zone: string, withDate: boolean): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(instant));
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  const time = `${part('hour')}:${part('minute')}`;
  return withDate ? `${part('year')}-${part('month')}-${part('day')} ${time}` : time;
}

/**
 * An instant as the member's local time: "08:00 in America/New_York". A zone that was
 * not read, is not on record, or is not a real zone is SAID, and the time is given in
 * UTC — never guessed. The date is added when the instant is a day or more after `from`.
 */
export function localTime(instant: string, zone: string | null | undefined, from?: string): string {
  const withDate = from !== undefined && Date.parse(instant) - Date.parse(from) >= 24 * 3600_000;
  const utc = `${wallTime(instant, 'UTC', withDate)} UTC`;
  if (zone === undefined) return `${utc} (member time zone not read)`;
  if (zone === null || zone === '') return `${utc} (no member time zone on record)`;
  try {
    return `${wallTime(instant, zone, withDate)} in ${zone}`;
  } catch {
    return `${utc} (time zone "${zone}" not recognised)`;
  }
}

function statusSentence(to: string, who: string | null, ctx: DescribeContext): string {
  switch (to) {
    case 'presented':
      if (who) return `${who} presented the match`;
      return ctx.hiddenUntilShown ? 'Match created — hidden until its summary was ready' : 'Status set to presented';
    case 'interested':
      return who ? `${who} showed interest` : 'Marked interested';
    case 'maybe':
      return who ? `${who} answered maybe` : 'Marked maybe';
    case 'passed':
      return who ? `${who} declined` : 'Declined';
    case 'mutual':
      return who ? `${who} showed interest — both members are interested` : 'Both members are interested';
    case 'connected':
      return who ? `${who} connected` : 'Connected';
    case 'expired':
      return who ? `${who} expired the match` : 'Expired — the decision deadline passed';
    case 'unmatched':
      return who ? `${who} unmatched` : 'Match closed';
    default:
      return who ? `${who} changed the status to ${to}` : `Status changed to ${to}`;
  }
}

function describePush(e: JourneyPushEvent, who: string): { text: string; detail?: string } {
  const zone = e.time_zone;
  switch (e.outcome) {
    case 'held': {
      const why = HOLD_REASON[e.reason] ?? e.reason;
      const until = e.held_until ? `until ${localTime(e.held_until, zone, e.at)}` : 'until an unrecorded time';
      const text = `Push to ${who} held — ${why}, ${until}${e.still_held ? ' (still waiting)' : ''}`;
      const detail =
        e.attempts > 0
          ? `Held again ${e.attempts} ${e.attempts === 1 ? 'time' : 'times'}; the reason above is the last one recorded.`
          : undefined;
      return { text, detail };
    }
    case 'sent': {
      const vendor = vendorOf(e.platform);
      const result =
        e.receipt === null
          ? `Expo accepted it; no result from ${vendor} recorded`
          : e.receipt === 'ok'
            ? `${vendor} accepted it`
            : e.receipt === 'expired_unknown'
              ? 'no delivery result: no receipt within 24 h'
              : `${vendor} refused it: ${e.receipt}`;
      return { text: `Push sent to ${who} — ${result} (${localTime(e.at, zone)})` };
    }
    case 'cancelled': {
      const why = e.outbox_outcome ? OUTBOX_OUTCOME[e.outbox_outcome] ?? e.outbox_outcome : 'no reason recorded';
      return { text: `Push to ${who} cancelled — ${why}` };
    }
    case 'exhausted': {
      const why = e.outbox_outcome ? OUTBOX_OUTCOME[e.outbox_outcome] ?? e.outbox_outcome : 'no reason recorded';
      return { text: `Push to ${who} not sent — the system gave up: ${why}`, detail: `${e.attempts} attempts` };
    }
    case 'not_sent': {
      if (e.reason) return { text: `No push to ${who} — ${pushErrorWords(e.reason)}` };
      const old = Date.parse(e.at) < Date.parse(PUSH_STAMPED_FROM);
      return {
        text: `No push recorded for ${who} — the card does not say why`,
        detail: old
          ? 'Cards created before 2026-08-18 did not always record their push, so one may still have gone.'
          : undefined,
      };
    }
  }
  return { text: `Push to ${who}: ${String((e as { outcome?: unknown }).outcome)}` };
}

/** One plain sentence for a step, and an optional exact detail line under it. */
export function describeJourneyEvent(
  e: JourneyEvent,
  names: Names,
  ctx: DescribeContext = {},
): { text: string; detail?: string } {
  switch (e.kind) {
    case 'created':
      return { text: 'Match created' };
    case 'restarted':
      return { text: 'Match restarted — the matched time was set again. Steps above it are from the earlier run.' };
    case 'status': {
      const detail = `${e.from_status ?? '—'} → ${e.to_status}${e.client ? ` · ${e.client}` : ''}${e.reason ? ` · ${e.reason}` : ''}`;
      return { text: statusSentence(e.to_status, actorName(e.actor, names), ctx), detail };
    }
    case 'shown': {
      const text = `Match made visible to ${names.nameA} and ${names.nameB} in the app`;
      if (e.created_for === undefined) return { text };
      const detail =
        e.created_for === null
          ? 'Two-sided record (created for both members)'
          : e.created_for === 'other'
            ? 'Created for a member who is not in this match'
            : `Created for ${actorName(e.created_for, names)} (one-sided record)`;
      return { text, detail };
    }
    case 'pictures_shared':
      return { text: `${actorName(e.actor, names)} shared pictures` };
    case 'pictures_viewed': {
      const other = e.actor === 'A' ? names.nameB : names.nameA;
      return { text: `${actorName(e.actor, names)} viewed ${other}'s pictures` };
    }
    case 'notification': {
      const who = actorName(e.actor, names);
      const type = `"${e.notification_type}"`;
      switch (e.step) {
        case 'sent':
          // Delivery is told by the push steps. This is only the card.
          return e.card_suppressed
            ? { text: `Push-only record ${type} for ${who} (no in-app card)` }
            : { text: `In-app card ${type} created for ${who}` };
        case 'received':
          return { text: `${who}'s app recorded the ${type} push arriving (the app was running)` };
        case 'opened':
          if (e.via === 'push') return { text: `${who} opened the ${type} push` };
          if (e.via === 'inbox') {
            return { text: `${who} opened the ${type} card in the app's notification list or the Abby screen notice` };
          }
          return { text: `${who} opened the ${type} card (the app did not record whether from the push or the in-app list)` };
        case 'dismissed':
          return { text: `${who} dismissed the ${type} push` };
        case 'read':
          return { text: `${who} marked the ${type} card as read` };
      }
      return { text: `${who}: ${type} ${String((e as { step?: unknown }).step)}` };
    }
    case 'push':
      return describePush(e, actorName(e.actor, names) ?? 'a member');
    case 'app_view': {
      const who = actorName(e.actor, names);
      if (e.client === 'mobile') return { text: `${who}'s app showed the match (first time)` };
      return {
        text: `The first fetch of the match for ${who} came from ${e.client ?? 'an unknown client'}, not the app — a later app view is not recorded`,
      };
    }
  }
  return { text: `Other recorded step (${String((e as { kind?: unknown }).kind)})` };
}

/**
 * The null → presented transitions that happened while the match was still HIDDEN:
 * presented_at (the `shown` step) came more than a minute later, with no restart and no
 * other status change between them. PROD 96784e0a: created 03:05:10, shown 03:10:22.
 */
export function hiddenUntilShown(events: JourneyEvent[]): Set<JourneyEvent> {
  const hidden = new Set<JourneyEvent>();
  const shown = events.find((e) => e.kind === 'shown');
  const first = events.find((e) => e.kind === 'status' && e.from_status === null && e.to_status === 'presented');
  if (!shown || !first) return hidden;
  const from = Date.parse(first.at);
  const to = Date.parse(shown.at);
  if (!(to - from > SAME_MOMENT_MS)) return hidden;
  const between = events.some((e) => {
    if (e === first || (e.kind !== 'status' && e.kind !== 'restarted')) return false;
    const t = Date.parse(e.at);
    return t >= from && t <= to;
  });
  if (!between) hidden.add(first);
  return hidden;
}

/** "+7m", "+3h 31m", "+2d 3h" — the time since the match was created. */
export function sinceStart(at: string, start: string | null): string {
  if (!start) return '';
  const ms = Date.parse(at) - Date.parse(start);
  if (Number.isNaN(ms)) return '';
  if (ms < 0) return '−' + sinceStart(start, at).slice(1);
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `+${d}d ${h}h`;
  if (h > 0) return `+${h}h ${m}m`;
  if (m > 0) return `+${m}m`;
  return `+${s}s`;
}

export function MatchJourneyCard({ journey, nameA, nameB }: { journey: MatchJourney | undefined } & Names) {
  if (!journey) return null;
  const names = { nameA, nameB };
  const start = journey.events.find((e) => e.kind === 'created')?.at ?? journey.events[0]?.at ?? null;
  const hidden = hiddenUntilShown(journey.events);

  return (
    <div className="mb-2 rounded-md border bg-card p-3 text-card-foreground">
      <div className="mb-2 font-semibold">Journey</div>

      {journey.unavailable.length > 0 && (
        <div className="mb-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-2 text-[13px] text-amber-600 dark:text-amber-400">
          This journey is incomplete. Could not read:{' '}
          {journey.unavailable.map((s) => SOURCE_LABEL[s] ?? s).join(', ')}.
        </div>
      )}

      <ol className="m-0 list-none p-0">
        {journey.events.map((e, i) => {
          const { text, detail } = describeJourneyEvent(e, names, { hiddenUntilShown: hidden.has(e) });
          return (
            <li key={`${e.at}-${i}`} className="flex gap-3 border-b py-1.5 text-[13px] last:border-b-0">
              <div className="w-[150px] shrink-0 text-muted-foreground">
                <div>{new Date(e.at).toLocaleString()}</div>
                <div className="text-[11px]">{e.kind === 'created' ? '' : sinceStart(e.at, start)}</div>
              </div>
              <div className="min-w-0">
                <div>{text}</div>
                {detail && <div className="text-[11px] text-muted-foreground">{detail}</div>}
              </div>
            </li>
          );
        })}
      </ol>

      <div className="mt-2 space-y-1 text-[11px] text-muted-foreground">
        <p className="m-0">
          Recorded in our database: status changes, pictures shared and viewed, in-app cards and when they were
          opened, each push (held, sent or not sent) with Apple's or Google's answer, and the first time each
          member's app showed the match.
        </p>
        <p className="m-0">
          Not recorded: later views of the match, views in the app's match list or match-detail page, and whether a
          push banner appeared on the phone — Apple or Google accepting a push does not mean the phone displayed it.
          Older app versions do not record whether a card was opened from the push or from the in-app list.
        </p>
        <p className="m-0">Local times use each member's time zone as it is today.</p>
      </div>
    </div>
  );
}
